import type { Shard } from '@pactswap/cwap-cm-lib/shared';
import BnbIcon from 'cryptocurrency-icons/svg/color/bnb.svg';
import BtcIcon from 'cryptocurrency-icons/svg/color/btc.svg';
import EthIcon from 'cryptocurrency-icons/svg/color/eth.svg';
import LtcIcon from 'cryptocurrency-icons/svg/color/ltc.svg';
import PolIcon from 'cryptocurrency-icons/svg/color/matic.svg';
import UsdcETHIcon from 'cryptocurrency-icons/svg/color/usdc.svg';
import UsdcPOLIcon from 'cryptocurrency-icons/svg/color/usdc.svg';
import UsdtTRXIcon from 'cryptocurrency-icons/svg/color/usdt.svg';
import UsdtETHIcon from 'cryptocurrency-icons/svg/color/usdt.svg';
import UsdtPOLIcon from 'cryptocurrency-icons/svg/color/usdt.svg';
import WbtcETHIcon from 'cryptocurrency-icons/svg/color/wbtc.svg';
import EthArbIcon from 'cryptocurrency-icons/svg/white/eth.svg';
import EthBASEIcon from 'cryptocurrency-icons/svg/white/eth.svg';
import UsdcBNBcon from 'cryptocurrency-icons/svg/white/usdc.svg';
import UsdcARBcon from 'cryptocurrency-icons/svg/white/usdc.svg';
import UsdcBASEIcon from 'cryptocurrency-icons/svg/white/usdc.svg';
import UsdtBNBIcon from 'cryptocurrency-icons/svg/white/usdt.svg';
import UsdtARBIcon from 'cryptocurrency-icons/svg/white/usdt.svg';
import UsdtBASEIcon from 'cryptocurrency-icons/svg/white/usdt.svg';
import WbtcBNBcon from 'cryptocurrency-icons/svg/white/wbtc.svg';
import { Address } from 'viem';
import { mainnet, bsc, arbitrum, polygon, tron, base } from 'viem/chains';

export enum Currency {
  BTC = 'BTC',
  LTC = 'LTC',
  EGLD = 'EGLD',
  ETH = 'ETH',
  BNB = 'BNB',
  USDT_ETH = 'USDT_ETH',
  USDT_BNB = 'USDT_BNB',
  TRX = 'TRX',
  USDT_TRX = 'USDT_TRX',
  WBTC_ETH = 'WBTC_ETH',
  WBTC_BNB = 'WBTC_BNB',
  USDC_ETH = 'USDC_ETH',
  USDC_BNB = 'USDC_BNB',
  USD1_ETH = 'USD1_ETH',
  USD1_BNB = 'USD1_BNB',
  DOGE = 'DOGE',
  POL = 'POL',
  USDT_POL = 'USDT_POL',
  USDC_POL = 'USDC_POL',
  ARB = 'ARB',
  ETH_ARB = 'ETH_ARB',
  USDT_ARB = 'USDT_ARB',
  USDC_ARB = 'USDC_ARB',
  ETH_BASE = 'ETH_BASE',
  USDT_BASE = 'USDT_BASE',
  USDC_BASE = 'USDC_BASE',
  CWEB = 'CWEB', // L2 token
  WCWEB_ETH = 'WCWEB_ETH', // wrapped cweb - ERC20
  SOL = 'SOL',
  USDT_SOL = 'USDT_SOL',
  USDC_SOL = 'USDC_SOL',
}

const tokens = Object.values(Currency);
export const supportedTokens = ((import.meta.env['VITE_SUPPORTED_TOKENS'] ?? '') as string)
  .split(',')
  .map((val) => val.trim())
  .filter((val): val is Currency => (tokens as string[]).includes(val));

export const NATIVE_TOKEN_TYPE = 'native';
export const ERC20_TOKEN_TYPE = 'erc20';
export const TRC20_TOKEN_TYPE = 'trc20';
export const BEP20_TOKEN_TYPE = 'bep20';
export const POL20_TOKEN_TYPE = 'pol20';
export const SPL_TOKEN_TYPE = 'spl';

export type TokenData = {
  name: Currency;
  fullName: string;
  type:
    | typeof NATIVE_TOKEN_TYPE
    | typeof ERC20_TOKEN_TYPE
    | typeof TRC20_TOKEN_TYPE
    | typeof BEP20_TOKEN_TYPE
    | typeof POL20_TOKEN_TYPE
    | typeof SPL_TOKEN_TYPE;
  chainId?: number;
};

export const tokensData: Record<Currency, TokenData> = {
  [Currency.BNB]: { name: Currency.BNB, fullName: 'Binance Coin', type: NATIVE_TOKEN_TYPE, chainId: bsc.id },
  [Currency.USDT_BNB]: { name: Currency.USDT_BNB, fullName: 'Tether', type: BEP20_TOKEN_TYPE, chainId: bsc.id },
  [Currency.USDC_BNB]: { name: Currency.USDC_BNB, fullName: 'USD Coin', type: BEP20_TOKEN_TYPE, chainId: bsc.id },
  [Currency.WBTC_BNB]: {
    name: Currency.WBTC_BNB,
    fullName: 'Wrapped Bitcoin',
    type: BEP20_TOKEN_TYPE,
    chainId: bsc.id,
  },
  [Currency.BTC]: { name: Currency.BTC, fullName: 'Bitcoin', type: NATIVE_TOKEN_TYPE },
  [Currency.ETH]: { name: Currency.ETH, fullName: 'Ether', type: NATIVE_TOKEN_TYPE, chainId: mainnet.id },
  [Currency.USDT_ETH]: { name: Currency.USDT_ETH, fullName: 'Tether', type: ERC20_TOKEN_TYPE, chainId: mainnet.id },
  [Currency.USDC_ETH]: { name: Currency.USDC_ETH, fullName: 'USD Coin', type: ERC20_TOKEN_TYPE, chainId: mainnet.id },
  [Currency.WBTC_ETH]: {
    name: Currency.WBTC_ETH,
    fullName: 'Wrapped Bitcoin',
    type: ERC20_TOKEN_TYPE,
    chainId: mainnet.id,
  },
  [Currency.LTC]: { name: Currency.LTC, fullName: 'Litecoin', type: NATIVE_TOKEN_TYPE },
  [Currency.EGLD]: { name: Currency.EGLD, fullName: 'Electronic Gold', type: NATIVE_TOKEN_TYPE },
  [Currency.TRX]: { name: Currency.TRX, fullName: 'Tron', type: NATIVE_TOKEN_TYPE, chainId: tron.id },
  [Currency.USDT_TRX]: { name: Currency.USDT_TRX, fullName: 'Tether', type: TRC20_TOKEN_TYPE, chainId: tron.id },
  [Currency.USD1_ETH]: { name: Currency.USD1_ETH, fullName: 'USD1', type: ERC20_TOKEN_TYPE, chainId: mainnet.id },
  [Currency.USD1_BNB]: { name: Currency.USD1_BNB, fullName: 'USD1', type: BEP20_TOKEN_TYPE, chainId: bsc.id },
  [Currency.DOGE]: { name: Currency.DOGE, fullName: 'Dogecoin', type: NATIVE_TOKEN_TYPE },
  [Currency.POL]: { name: Currency.POL, fullName: 'Polygon', type: NATIVE_TOKEN_TYPE, chainId: polygon.id },
  [Currency.USDT_POL]: { name: Currency.USDT_POL, fullName: 'Tether', type: POL20_TOKEN_TYPE, chainId: polygon.id },
  [Currency.USDC_POL]: { name: Currency.USDC_POL, fullName: 'USD Coin', type: POL20_TOKEN_TYPE, chainId: polygon.id },
  [Currency.ARB]: { name: Currency.ARB, fullName: 'Arbitrum', type: ERC20_TOKEN_TYPE, chainId: arbitrum.id },
  [Currency.USDT_ARB]: { name: Currency.USDT_ARB, fullName: 'Tether', type: ERC20_TOKEN_TYPE, chainId: arbitrum.id },
  [Currency.USDC_ARB]: { name: Currency.USDC_ARB, fullName: 'USD Coin', type: ERC20_TOKEN_TYPE, chainId: arbitrum.id },
  [Currency.ETH_ARB]: { name: Currency.ETH_ARB, fullName: 'Ether', type: NATIVE_TOKEN_TYPE, chainId: arbitrum.id },
  [Currency.USDT_BASE]: { name: Currency.USDT_BASE, fullName: 'Tether', type: ERC20_TOKEN_TYPE, chainId: base.id },
  [Currency.USDC_BASE]: { name: Currency.USDC_BASE, fullName: 'USD Coin', type: ERC20_TOKEN_TYPE, chainId: base.id },
  [Currency.ETH_BASE]: { name: Currency.ETH_BASE, fullName: 'Ether', type: NATIVE_TOKEN_TYPE, chainId: base.id },
  [Currency.CWEB]: { name: Currency.CWEB, fullName: 'Coinweb', type: NATIVE_TOKEN_TYPE },
  [Currency.WCWEB_ETH]: {
    name: Currency.WCWEB_ETH,
    fullName: 'Wrapped Coinweb',
    type: ERC20_TOKEN_TYPE,
    chainId: mainnet.id,
  },
  [Currency.SOL]: { name: Currency.SOL, fullName: 'Solana', type: NATIVE_TOKEN_TYPE },
  [Currency.USDT_SOL]: { name: Currency.USDT_SOL, fullName: 'Tether', type: SPL_TOKEN_TYPE },
  [Currency.USDC_SOL]: { name: Currency.USDC_SOL, fullName: 'USD Coin', type: SPL_TOKEN_TYPE },
  // Add more tokens as needed
};

export const TOKENS = [
  ...new Set(supportedTokens.filter((name): name is keyof typeof tokensData => name in tokensData)),
].map((token) => tokensData[token]);

export const EVM_NATIVE_TOKENS = [
  Currency.ETH,
  Currency.BNB,
  Currency.POL,
  Currency.ETH_ARB,
  Currency.ETH_BASE,
] as const satisfies Currency[];

export const ERC20_TOKENS = [
  Currency.USDT_ETH,
  Currency.USDT_BNB,
  Currency.USDT_POL,
  Currency.USDC_ETH,
  Currency.USDC_BNB,
  Currency.USDC_POL,
  Currency.WBTC_ETH,
  Currency.WBTC_BNB,
  Currency.USD1_ETH,
  Currency.USD1_BNB,
  Currency.WCWEB_ETH,
  Currency.ARB,
  Currency.USDT_ARB,
  Currency.USDC_ARB,
  Currency.USDT_BASE,
  Currency.USDC_BASE,
] as const satisfies Currency[];

export const BTC_LIKE_TOKENS = [Currency.BTC, Currency.LTC, Currency.DOGE] as const satisfies Currency[];

export const SOLANA_NATIVE_TOKENS = [Currency.SOL] as const satisfies Currency[];

export const SPL_TOKENS = [Currency.USDT_SOL, Currency.USDC_SOL] as const satisfies Currency[];

type Oracle = {
  address: Address;
  chainId: typeof mainnet.id | typeof bsc.id;
};

// Contarct addresses: https://docs.chain.link/data-feeds/price-feeds/addresses
export const TOKEN_TO_ORACLE: Record<Exclude<Currency, Currency.CWEB | Currency.WCWEB_ETH>, Oracle> = {
  [Currency.BTC]: { address: '0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c', chainId: 1 },
  [Currency.LTC]: { address: '0x74E72F37A8c415c8f1a98Ed42E78Ff997435791D', chainId: 56 },
  [Currency.DOGE]: { address: '0x3AB0A0d137D4F946fBB19eecc6e92E64660231C8', chainId: 56 },
  [Currency.EGLD]: { address: '0x0000000000000000000000000000000000000000', chainId: 1 },
  [Currency.ETH]: { address: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419', chainId: 1 },
  [Currency.BNB]: { address: '0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE', chainId: 56 },
  [Currency.USDT_ETH]: { address: '0x3E7d1eAB13ad0104d2750B8863b489D65364e32D', chainId: 1 },
  [Currency.USDT_BNB]: { address: '0xB97Ad0E74fa7d920791E90258A6E2085088b4320', chainId: 56 },
  [Currency.TRX]: { address: '0xF4C5e535756D11994fCBB12Ba8adD0192D9b88be', chainId: 56 },
  [Currency.USDT_TRX]: { address: '0xB97Ad0E74fa7d920791E90258A6E2085088b4320', chainId: 56 },
  [Currency.WBTC_ETH]: { address: '0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c', chainId: 1 },
  [Currency.WBTC_BNB]: { address: '0x264990fbd0A4796A3E3d8E37C4d5F87a3aCa5Ebf', chainId: 56 },
  [Currency.USDC_BNB]: { address: '0x51597f405303C4377E36123cBc172b13269EA163', chainId: 56 },
  [Currency.USDC_ETH]: { address: '0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6', chainId: 1 },
  [Currency.USD1_ETH]: { address: '0xF0d9bb015Cd7BfAb877B7156146dc09Bf461370d', chainId: 1 },
  [Currency.USD1_BNB]: { address: '0xaD8b4e59A7f25B68945fAf0f3a3EAF027832FFB0', chainId: 56 },
  [Currency.POL]: { address: '0x081195B56674bb87b2B92F6D58F7c5f449aCE19d', chainId: 56 },
  [Currency.USDT_POL]: { address: '0xB97Ad0E74fa7d920791E90258A6E2085088b4320', chainId: 56 },
  [Currency.USDC_POL]: { address: '0x51597f405303C4377E36123cBc172b13269EA163', chainId: 56 },
  [Currency.ARB]: { address: '0x31697852a68433DbCc2Ff612c516d69E3D9bd08F', chainId: 1 },
  [Currency.USDT_ARB]: { address: '0xB97Ad0E74fa7d920791E90258A6E2085088b4320', chainId: 56 },
  [Currency.USDC_ARB]: { address: '0x51597f405303C4377E36123cBc172b13269EA163', chainId: 56 },
  [Currency.ETH_ARB]: { address: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419', chainId: 1 },
  [Currency.USDT_BASE]: { address: '0xB97Ad0E74fa7d920791E90258A6E2085088b4320', chainId: 56 },
  [Currency.USDC_BASE]: { address: '0x51597f405303C4377E36123cBc172b13269EA163', chainId: 56 },
  [Currency.ETH_BASE]: { address: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419', chainId: 1 },
  [Currency.SOL]: { address: '0x0E8a53DD9c13589df6382F13dA6B3Ec8F919B323', chainId: 1 },
  [Currency.USDT_SOL]: { address: '0xB97Ad0E74fa7d920791E90258A6E2085088b4320', chainId: 1 },
  [Currency.USDC_SOL]: { address: '0x51597f405303C4377E36123cBc172b13269EA163', chainId: 1 },
};

export const COINGECKO_COINS: { [key in Currency]: string } = {
  BNB: 'binancecoin',
  ETH: 'ethereum',
  BTC: 'bitcoin',
  USDT_BNB: 'tether',
  USDT_ETH: 'tether',
  LTC: 'litecoin',
  CWEB: 'coinweb',
  WCWEB_ETH: 'coinweb',
  EGLD: 'elrond-erd-2',
  TRX: 'tron',
  USDT_TRX: 'tether',
  WBTC_ETH: 'wrapped-bitcoin',
  WBTC_BNB: 'berachain-bridged-wbtc-berachain',
  USDC_ETH: 'usd-coin',
  USDC_BNB: 'binance-bridged-usdc-bnb-smart-chain',
  USD1_ETH: 'usd1-wlfi',
  USD1_BNB: 'usd1-wlfi',
  DOGE: 'dogecoin',
  POL: 'polygon-ecosystem-token',
  USDT_POL: 'tether',
  USDC_POL: 'usd-coin',
  ARB: 'arbitrum',
  USDT_ARB: 'tether',
  USDC_ARB: 'usd-coin',
  ETH_ARB: 'ethereum',
  USDT_BASE: 'tether',
  USDC_BASE: 'usd-coin',
  ETH_BASE: 'ethereum',
  SOL: 'solana',
  USDT_SOL: 'tether',
  USDC_SOL: 'usd-coin',
};

export const TOKEN_ICONS: { [key in Partial<Currency>]: string } = {
  BTC: BtcIcon,
  LTC: LtcIcon,
  EGLD: '/chains/multiversx.svg',
  BNB: BnbIcon,
  ETH: EthIcon,
  USDT_ETH: UsdtETHIcon,
  USDT_BNB: UsdtBNBIcon,
  CWEB: '/chains/coinweb_token.png',
  WCWEB_ETH: '/chains/coinweb_token.png',
  TRX: '/chains/tron.svg',
  USDT_TRX: UsdtTRXIcon,
  WBTC_ETH: WbtcETHIcon,
  WBTC_BNB: WbtcBNBcon,
  USDC_ETH: UsdcETHIcon,
  USDC_BNB: UsdcBNBcon,
  USD1_ETH: '/chains/usd1.png',
  USD1_BNB: '/chains/usd1.png',
  DOGE: '/chains/doge.svg',
  POL: PolIcon,
  USDT_POL: UsdtPOLIcon,
  USDC_POL: UsdcPOLIcon,
  ARB: '/chains/arb.svg',
  USDT_ARB: UsdtARBIcon,
  USDC_ARB: UsdcARBcon,
  ETH_ARB: EthArbIcon,
  USDT_BASE: UsdtBASEIcon,
  USDC_BASE: UsdcBASEIcon,
  ETH_BASE: EthBASEIcon,
  SOL: '/chains/solana.svg',
  USDT_SOL: UsdtETHIcon,
  USDC_SOL: UsdcETHIcon,
};

export type TempExtendedShard = Shard | 'base';

export const TOKENS_TO_SHARD: Record<Exclude<Currency, Currency.CWEB>, TempExtendedShard> = {
  [Currency.ETH]: 'eth',
  [Currency.BNB]: 'bnb',
  [Currency.POL]: 'matic',
  [Currency.BTC]: 'btc',
  [Currency.LTC]: 'ltc',
  [Currency.DOGE]: 'dog',
  [Currency.EGLD]: 'egld',
  [Currency.TRX]: 'trx',
  [Currency.ARB]: 'arb',
  [Currency.USDT_ETH]: 'eth',
  [Currency.USDT_BNB]: 'bnb',
  [Currency.USDT_POL]: 'matic',
  [Currency.USDC_ETH]: 'eth',
  [Currency.USDC_BNB]: 'bnb',
  [Currency.USDC_POL]: 'matic',
  [Currency.WBTC_ETH]: 'eth',
  [Currency.WBTC_BNB]: 'bnb',
  [Currency.USD1_ETH]: 'eth',
  [Currency.USD1_BNB]: 'bnb',
  [Currency.USDT_TRX]: 'trx',
  [Currency.USDT_ARB]: 'arb',
  [Currency.USDC_ARB]: 'arb',
  [Currency.ETH_ARB]: 'arb',
  [Currency.WCWEB_ETH]: 'eth',
  [Currency.USDT_BASE]: 'base',
  [Currency.USDC_BASE]: 'base',
  [Currency.ETH_BASE]: 'base',
  [Currency.SOL]: 'sol',
  [Currency.USDT_SOL]: 'sol',
  [Currency.USDC_SOL]: 'sol',
};

export const DEFAULT_FIRST_TOKEN = Currency.ETH;
export const DEFAULT_SECOND_TOKEN = Currency.BNB;
